import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import 'common/src/components/tableSkeleton/index.scss';

/** 行级骨架占位记录上的标记字段，仅在 CustomAdvancedTable 内使用 */
export const CUSTOM_ADVANCED_TABLE_SKELETON_KEY = '__customAdvancedTableSkeleton' as const;

export function isCustomAdvancedTableSkeletonRecord(record: unknown): boolean {
    return Boolean((record as Record<string, unknown>)?.[CUSTOM_ADVANCED_TABLE_SKELETON_KEY]);
}

export function buildSkeletonRecordList<T extends object>(rowCount: number, checkKey: string): T[] {
    return Array.from({ length: rowCount }, (_, i) => {
        const row = {
            [checkKey]: `__customAdvancedTableSk_${i}`,
            [CUSTOM_ADVANCED_TABLE_SKELETON_KEY]: true,
        } as Record<string, unknown>;
        return row as unknown as T;
    });
}

export interface IWrapSkeletonColumnsOptions {
    showStripes?: boolean;
    cellHeight?: number | string;
    /** 骨架行容器高度，默认 48px；应与数据行实际行高一致以避免切换时抖动 */
    rowHeight?: number | string;
}

/** 为 Ant Table 列树叶子节点包一层 render：骨架行时统一渲染单元格占位 */
export function wrapAntColumnsForSkeletonRows(
    cols: Record<string, unknown>[],
    options: IWrapSkeletonColumnsOptions,
): Record<string, unknown>[] {
    const { showStripes = true, cellHeight = 20, rowHeight = 48 } = options;
    return cols.map((col) => {
        const c = col as Record<string, unknown> & {
            children?: Record<string, unknown>[];
            render?: (value: unknown, record: object, index: number) => ReactNode;
        };
        if (c.children?.length) {
            return {
                ...c,
                children: wrapAntColumnsForSkeletonRows(c.children, options),
            };
        }
        const prevRender = c.render;
        return {
            ...c,
            render(value: unknown, record: object, index: number) {
                if (isCustomAdvancedTableSkeletonRecord(record)) {
                    return (
                        <TableBodySkeletonCell
                            rowIndex={index}
                            showStripes={showStripes}
                            cellHeight={cellHeight}
                            rowHeight={rowHeight}
                        />
                    );
                }
                if (prevRender) {
                    return prevRender(value, record, index);
                }
                return value as ReactNode;
            },
        };
    });
}

function TableBodySkeletonCell({
    rowIndex,
    showStripes,
    cellHeight,
    rowHeight,
}: {
    rowIndex: number;
    showStripes: boolean;
    cellHeight: number | string;
    rowHeight: number | string;
}) {
    const itemClass = useMemo(() => {
        let className = 'umu-skeleton-item';
        if (showStripes) {
            className += ' umu-skeleton-stripes';
        }
        return className;
    }, [showStripes]);

    return (
        <div
            style={{
                padding: '0 24px',
                display: 'flex',
                alignItems: 'center',
                height: typeof rowHeight === 'number' ? `${rowHeight}px` : rowHeight,
                boxSizing: 'border-box',
            }}
        >
            <div
                className={itemClass}
                style={{
                    marginBottom: 0,
                    width: rowIndex % 2 === 0 ? '40%' : '50%',
                    height: cellHeight,
                }}
            />
        </div>
    );
}
